import { Loader2, Upload, CheckCircle2, AlertTriangle, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils/cn'
import type { CommitStatus } from '../../lib/mapping-types'
import type { ImportCommitResult } from '../../api/data-import-api'

export interface CommitSummary {
  totalRecords: number
  totalInstances: number
  validInstances: number
  errorInstances: number
}

export function StepCommit({
  summary,
  commitStatus,
  busy,
  progress,
  result,
  onCommit,
  onDownloadReport,
}: {
  summary: CommitSummary
  commitStatus: CommitStatus
  busy: boolean
  progress: { done: number; total: number }
  result: ImportCommitResult | null
  onCommit: () => void
  onDownloadReport: () => void
}) {
  if (result) {
    const allOk = result.failed === 0 && result.skipped === 0
    return (
      <div className="space-y-4">
        <div className={cn('rounded-lg p-4 flex items-center gap-3', allOk ? 'bg-emerald-500/10' : 'bg-yellow-500/10')}>
          {allOk ? <CheckCircle2 className="w-6 h-6 text-emerald-500" /> : <AlertTriangle className="w-6 h-6 text-yellow-500" />}
          <div>
            <p className="font-semibold">{allOk ? 'ورود با موفقیت انجام شد' : 'ورود با موارد نیازمند توجه'}</p>
            <p className="text-sm text-muted-foreground">
              {result.recordsCreated} رکورد جدید، {result.recordsMatched} رکورد تطبیق‌یافته،{' '}
              {result.submissionsCreated} ثبت ایجاد، {result.submissionsUpdated} ثبت به‌روز شد.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Metric label="رکورد جدید" value={result.recordsCreated} />
          <Metric label="تطبیق‌یافته" value={result.recordsMatched} />
          <Metric label="ثبت ایجادشده" value={result.submissionsCreated} />
          <Metric label="ناموفق/رد‌شده" value={result.failed + result.skipped} danger={result.failed + result.skipped > 0} />
        </div>

        {(result.failed > 0 || result.skipped > 0) && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 max-h-48 overflow-y-auto text-xs space-y-1">
            {result.rows
              .filter((r) => r.action === 'failed' || r.action === 'skipped')
              .slice(0, 100)
              .map((r, i) => (
                <div key={i} className="text-muted-foreground">
                  ردیف {r.sourceRowIndex + 1} — {r.action === 'skipped' ? 'رد شد' : 'ناموفق'}: {r.error}
                </div>
              ))}
          </div>
        )}

        <Button variant="outline" onClick={onDownloadReport} className="gap-1.5">
          <Download className="w-4 h-4" /> دانلود گزارش ورود (CSV)
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border p-4 space-y-2">
        <Row label="رکوردها" value={String(summary.totalRecords)} />
        <Row label="کل نمونه‌ها" value={String(summary.totalInstances)} />
        <Row label="نمونه‌های معتبر" value={String(summary.validInstances)} cls="text-emerald-500" />
        {summary.errorInstances > 0 && (
          <Row label="نمونه‌های خطادار (رد می‌شوند)" value={String(summary.errorInstances)} cls="text-destructive" />
        )}
        <Row label="وضعیت ثبت" value={commitStatus === 'submitted' ? 'ثبت‌شده' : 'پیش‌نویس'} />
      </div>

      {summary.errorInstances > 0 && (
        <p className="text-xs text-yellow-600 dark:text-yellow-400 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          {summary.errorInstances} نمونهٔ خطادار وارد نخواهند شد. برای اصلاح به مرحلهٔ بازبینی بازگردید.
        </p>
      )}

      {busy && (
        <div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1 text-center">
            {progress.done} از {progress.total}
          </p>
        </div>
      )}

      <Button onClick={onCommit} disabled={busy || summary.validInstances === 0} className="gap-1.5 w-full">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        ورود {summary.validInstances} نمونه
      </Button>
    </div>
  )
}

function Row({ label, value, cls }: { label: string; value: string; cls?: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('font-medium', cls)}>{value}</span>
    </div>
  )
}

function Metric({ label, value, danger }: { label: string; value: number; danger?: boolean }) {
  return (
    <div className="rounded-lg border border-border p-2 text-center">
      <p className={cn('text-lg font-semibold tabular-nums', danger && 'text-destructive')}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  )
}
